import { formatNumber } from '@/utils/formatters';
import React from 'react';
import { AreaChart, Legend, ResponsiveContainer, Text } from 'recharts';
import { Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import ChartLoadingOverlay from '../common/ChartLoadingOverlay';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const LeftYAxisLabel = ({
  viewBox,
  value,
  angle,
  style = {
    fontWeight: 600,
    fontSize: 10,
  },
  offset = 0,
}) => {
  const { x, y, height } = viewBox;
  return (
    <Text
      x={x + offset}
      y={(y * 1) / height + (height - 30) / 2}
      textAnchor="middle"
      angle={angle}
      style={style}
    >
      {value}
    </Text>
  );
};

function AppAreaChart({
  data,
  xDataKey,
  chartType,
  children,
  loading = false,
  chartMetrics,
  visibleMetrics,
  ...props
}) {
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <div className="w-full h-full relative">
      {loading && <ChartLoadingOverlay loading={loading} />}

      <ResponsiveContainer
        width="100%"
        height="100%"
        className="border bg-white rounded-md shadow-sm pr-2"
      >
        <AreaChart data={data} {...props} margin={{ left: 10 }}>
          <CartesianGrid strokeDasharray="3 0" />
          <Tooltip
            formatter={(value, name, props) => {
              return [
                formatNumber(value),
                name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
              ];
            }}
          />
          <Legend
            layout="horizontal"
            verticalAlign="top"
            align="center"
            wrapperStyle={{ paddingBottom: 8, fontSize: 12 }}
            formatter={(value) => {
              return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
            }}
          />
          <XAxis
            dataKey="date"
            label={{
              value: 'Charge Time',
              position: 'insideBottom',
              offset: 5,
              style: {
                fontWeight: 600,
                fontSize: 10,
              },
            }}
            tick={{ fontSize: isMobile ? 9 : 10 }}
          />
          <YAxis
            label={<LeftYAxisLabel value="Day Revenue" angle={-90} offset={10} />}
            tick={{ fontSize: isMobile ? 9 : 10 }}
            tickFormatter={(value) => formatNumber(value)}
          />
          {chartMetrics ? (
            chartMetrics.map((metric) => {
              if (!visibleMetrics.includes(metric.key)) return null;
              if (metric.type === 'area') {
                return (
                  <Area
                    key={metric.key}
                    type="monotone"
                    dataKey={metric.key}
                    stroke={metric.color}
                    fill={metric.color}
                    dot
                  />
                );
              }

              return null;
            })
          ) : (
            <>
              <Area type="monotone" dataKey="charge_total" stroke="#56a0eb" fill="#56a0eb" dot />
              <Area type="monotone" dataKey="charge_renew" stroke="#965076" fill="#965076" dot />
              <Area type="monotone" dataKey="charge_register" stroke="#9cd177" fill="#9cd177" dot />
            </>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default AppAreaChart;
